import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserRound, Building2, ArrowLeft, Sparkles, ShieldCheck, MapPin, Loader2, Plus, CircleCheck, FolderPlus, ArrowRight } from "lucide-react";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";
import ModuleSecuriteFinanciere from "@/pages/modules/ModuleSecuriteFinanciere";
import { DossierImesSection } from "@/components/dossier/DossierImesSection";
import { DossierBankabilitySection } from "@/components/dossier/DossierBankabilitySection";
import { DossierAnalyseHero } from "@/components/dossier/DossierAnalyseHero";
import { DossierInstitutions } from "@/components/dossier/DossierInstitutions";
import { DossierBilanHistory } from "@/components/dossier/DossierBilanHistory";
import VaultDocuments from "@/components/VaultDocuments";
import VaultIntelligenceCard from "@/components/VaultIntelligenceCard";
import VaultAidsPanel from "@/components/VaultAidsPanel";
import DossierCountryPicker from "@/components/DossierCountryPicker";
import RelationshipTimeline from "@/components/altiora/RelationshipTimeline";
import { DossierMeetingRecaps } from "@/components/dossier/DossierMeetingRecaps";

const CATEGORY_LABELS = {
  imes_trajectoire: "Trajectoire économique personnelle",
  credit: "Crédit",
  hypotheque: "Hypothèque",
  securite_financiere: "Sécurité financière",
  assurance: "Assurance",
  investissements: "Investissements",
  fiscalite: "Fiscalité",
  immobilier: "Immobilier",
  transmission: "Transmission",
  carriere: "Carrière",
  immigration: "Immigration",
};

/**
 * ALTIORA V3 · Dossier detail (Phase 0 amorce — minimal viable view)
 *
 * Phase 1 étendra cette page avec :
 *   - Sections dynamiques du questionnaire (economic_data)
 *   - Dashboard de progression
 *   - Activation/désactivation de modules avec gating abonnement + licence
 *   - Recommandations par module
 */
export default function DossierDetail() {
  const { dossier_id } = useParams();
  const navigate = useNavigate();

  const [dossier, setDossier] = useState(null);
  const [modules, setModules] = useState([]);
  const [children, setChildren] = useState([]);
  const [childArchetypes, setChildArchetypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState("");
  const [openedModule, setOpenedModule] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: mods }] = await Promise.all([
          api.get(`/dossiers/${dossier_id}`),
          api.get("/modules/"),
        ]);
        setDossier(d);
        setModules(mods.items || []);

        // Load children (best-effort — may 403 if no access)
        try {
          const { data: kids } = await api.get(`/dossiers/${dossier_id}/children`);
          setChildren(kids.items || []);
        } catch { /* no-op */ }

        // Load the elia module descriptor to know which child dossier archetypes are offered
        if (d.elia_module) {
          try {
            const { data: elia } = await api.get(`/elia-modules/${d.elia_module}`);
            setChildArchetypes(elia.child_dossier_types || []);
          } catch { /* no-op */ }
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Dossier introuvable.");
      } finally { setLoading(false); }
    })();
  }, [dossier_id]);
  const refreshDossier = async () => {
    try {
      const { data: d } = await api.get(`/dossiers/${dossier_id}`);
      setDossier(d);
    } catch { /* no-op */ }
  };

  const activateModule = async (moduleId) => {
    try {
      const { data } = await api.post(`/dossiers/${dossier_id}/modules/${moduleId}/activate`);
      setDossier((d) => ({ ...d, modules_active: data.modules_active }));
      toast.success("Module activé");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Activation impossible", { duration: 6000 });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7F3]">
        <div className="flex items-center gap-3 text-[#5B5B62] font-mono text-xs uppercase tracking-widest">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Chargement…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F7F3] px-6 pt-24 max-w-2xl mx-auto">
        <h1 className="font-display text-3xl text-[#1F2937]">Ce dossier n&apos;est pas accessible.</h1>
        <p className="mt-3 text-[#5B5B62]">{error}</p>
        <Link to="/app" className="mt-6 inline-flex items-center gap-2 text-[#0B2545] hover:underline">
          <ArrowLeft className="h-4 w-4" /> Retour
        </Link>
      </div>
    );
  }

  const isIndividual = dossier.type === "individual";
  const activeSet = new Set(dossier.modules_active || []);
  const availableModules = modules.filter((m) => m.client_types.includes(dossier.type));
  const isPending = dossier.status === "pending_client_approval";

  const approveDossier = async () => {
    setApproving(true);
    try {
      await api.post(`/dossiers/${dossier_id}/approve`);
      setDossier((d) => ({ ...d, status: "active" }));
      toast.success("Dossier approuvé et activé.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Approbation impossible");
    } finally { setApproving(false); }
  };

  const openChildFlow = (childType) => {
    const params = new URLSearchParams({
      subtype: childType.key,
      client_type: childType.default_client_type,
      parent_id: dossier_id,
    });
    if (dossier.elia_module) params.set("elia", dossier.elia_module);
    navigate(`/app/dossiers/new?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937]">
      <header className="border-b border-[#E4E1D8] bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AltioraTreeLogo size={36} />
            <Link to="/app" className="text-xs font-mono uppercase tracking-[0.24em] text-[#5B5B62] hover:text-[#0B2545]">
              <ArrowLeft className="inline h-3.5 w-3.5 mr-1 -mt-0.5" strokeWidth={2} /> Retour
            </Link>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#7A5B14]">
            Méthode ALTIORA · {dossier.method_version}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="dossier-detail-page">
        {/* Pending approval banner (Pro-created dossier) */}
        {isPending && (
          <div className="mb-8 rounded-xl border border-[#C5A880]/50 bg-[#FBF6EA] p-5" data-testid="dossier-pending-banner">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-[#7A5B14] mt-0.5" strokeWidth={1.8} />
              <div className="flex-1">
                <div className="font-display text-lg text-[#1F2937] tracking-tight">Ce dossier a été préparé pour vous</div>
                <p className="mt-1.5 text-sm text-[#5B5B62] leading-relaxed">
                  Un professionnel de votre réseau ALTIORA a créé ce dossier en votre nom.
                  Vous restez pleinement propriétaire — approuvez-le pour l&apos;activer.
                </p>
                <button
                  onClick={approveDossier}
                  disabled={approving}
                  data-testid="dossier-approve"
                  className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-4 py-2 text-sm font-medium transition-colors"
                >
                  {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} /> : <CircleCheck className="h-3.5 w-3.5" strokeWidth={2} />}
                  {approving ? "Activation…" : "Approuver et activer ce dossier"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="flex items-start gap-5">
          <div className={`h-14 w-14 rounded-xl flex items-center justify-center ${
            isIndividual ? "bg-[#0B2545]/10 text-[#0B2545]" : "bg-[#7A5B14]/10 text-[#7A5B14]"
          }`}>
            {isIndividual
              ? <UserRound className="h-7 w-7" strokeWidth={1.5} />
              : <Building2 className="h-7 w-7" strokeWidth={1.5} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
              {isIndividual ? "Personne physique" : "Personne morale"} · {dossier.jurisdiction}
            </div>
            <h1 className="mt-1 font-display text-3xl md:text-4xl tracking-tight text-[#1F2937]" data-testid="dossier-name">
              {dossier.name}
            </h1>
            {!isIndividual && dossier.legal_entity?.business_number && (
              <div className="mt-1 text-xs font-mono text-[#5B5B62]">NEQ · {dossier.legal_entity.business_number}</div>
            )}
            {isIndividual && dossier.individual?.residence_city && (
              <div className="mt-1 inline-flex items-center gap-1 text-xs text-[#5B5B62]">
                <MapPin className="h-3 w-3" strokeWidth={2} /> {dossier.individual.residence_city}
              </div>
            )}
            <div className="mt-4">
              <Link
                to={`/app/dossiers/${dossier_id}/questionnaire`}
                data-testid="dossier-open-questionnaire"
                className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-4 py-2 text-sm transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> Compléter le questionnaire
              </Link>
            </div>
          </div>
        </div>

        {/* Modules */}
        <section className="mt-12">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">Modules ALTIORA</div>
              <h2 className="mt-1 font-display text-2xl tracking-tight text-[#1F2937]">
                {dossier.modules_active?.length || 0} module{(dossier.modules_active?.length || 0) > 1 ? "s" : ""} activé{(dossier.modules_active?.length || 0) > 1 ? "s" : ""}
              </h2>
            </div>
          </div>

          {/* Active modules */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dossier-active-modules">
            {availableModules.filter((m) => activeSet.has(m.id)).map((m) => (
              <button
                key={m.id}
                onClick={() => setOpenedModule(m.id === openedModule ? "" : m.id)}
                data-testid={`dossier-module-open-${m.id}`}
                className="text-left"
              >
                <ModuleCard module={m} active testId={`dossier-module-${m.id}`} highlight={openedModule === m.id} />
              </button>
            ))}
          </div>

          {/* Opened module dashboard */}
          {openedModule === "securite_financiere" && activeSet.has("securite_financiere") && (
            <div className="mt-8 rounded-xl border border-[#0B2545]/20 bg-[#F8F7F3]/50 p-6" data-testid="dossier-opened-module-securite_financiere">
              <ModuleSecuriteFinanciere dossierId={dossier.dossier_id} dossier={dossier} />
            </div>
          )}
          {openedModule && openedModule !== "securite_financiere" && (
            <div className="mt-6 rounded-lg border border-dashed border-[#E4E1D8] bg-white/60 p-6 text-center text-sm text-[#5B5B62]">
              Module « {CATEGORY_LABELS[openedModule] || openedModule} » — tableau de bord dédié en cours de préparation.
              <br />
              <span className="text-xs">Cette module fait partie du plan de rollout V3 (Phases 4-5).</span>
            </div>
          )}

          {/* Available (inactive) modules */}
          <div className="mt-12">
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
              Modules disponibles pour ce type de dossier
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dossier-available-modules">
              {availableModules.filter((m) => !activeSet.has(m.id)).map((m) => (
                <button
                  key={m.id}
                  onClick={() => activateModule(m.id)}
                  data-testid={`dossier-module-activate-${m.id}`}
                  className="text-left rounded-xl border border-dashed border-[#E4E1D8] bg-white/60 hover:bg-white hover:border-[#0B2545] p-4 transition-all group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display text-sm text-[#1F2937] tracking-tight">{m.name_fr}</div>
                    <Plus className="h-4 w-4 text-[#B8B5AC] group-hover:text-[#0B2545] transition-colors" strokeWidth={2} />
                  </div>
                  <div className="mt-2 text-xs text-[#5B5B62] leading-relaxed line-clamp-2">{m.description_fr}</div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-[9px] font-mono uppercase tracking-widest bg-[#F1EFE8] text-[#5B5B62] rounded-full px-2 py-0.5">
                      {m.subscription_tier}
                    </span>
                    {m.required_licenses?.length > 0 && (
                      <span className="text-[9px] font-mono uppercase tracking-widest bg-[#FBF6EA] text-[#7A5B14] rounded-full px-2 py-0.5">
                        Habilitation requise
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Hero — Tout analyser + 4 tuiles score */}
        <DossierAnalyseHero dossier={dossier} onRefresh={refreshDossier} />

        {/* Sprint 10.P · Universal Relationship Timeline — chronologie complète du client */}
        <section className="mt-8" data-testid="dossier-timeline-section">
          <div className="rounded-md border border-[#E4E1D8] bg-white p-5">
            <RelationshipTimeline
              subjectType={dossier.type === "legal_entity" ? "organization" : "client"}
              subjectId={dossier.dossier_id}
            />
          </div>
        </section>

        {/* Sprint 10.Z · Recaps de réunions automatiques */}
        <DossierMeetingRecaps dossierId={dossier.dossier_id} />

        {/* Document Intelligence Vault — carte prominente (Iter58) */}
        <VaultIntelligenceCard dossierId={dossier.dossier_id} onRefresh={refreshDossier} />

        {/* IMES™ diagnostic — trajectoire économico-sociale (personnes physiques uniquement) */}
        {isIndividual && (
          <DossierImesSection dossier={dossier} onDossierUpdate={refreshDossier} />
        )}

        {/* Bancabilité ALTIORA™ — indice de préparation dossier */}
        <DossierBankabilitySection dossier={dossier} onDossierUpdate={refreshDossier} />

        {/* Comparateur multi-institutions (RBC, BDC, Desjardins, BPI, SBA…) */}
        <DossierInstitutions dossier={dossier} />

        {/* Sélecteur pays éditable — pilote les aides + le comparateur (Iter62) */}
        <div className="mt-12">
          <DossierCountryPicker dossier={dossier} onRefresh={refreshDossier} />
        </div>

        {/* Aides & programmes Chantier B — cross-check vault (Iter60) */}
        <VaultAidsPanel dossierId={dossier.dossier_id}
                         key={`aids-${(dossier?.individual?.country || dossier?.legal_entity?.country || 'CA')}`} />

        {/* Preuves documentaires — MVP Phase 1 (Formation / Expérience / Fiscalité) */}
        <section className="mt-14" data-testid="dossier-vault-section">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
                Preuves documentaires
              </div>
              <h2 className="mt-1 font-display text-2xl tracking-tight text-[#1F2937]">
                Vos pièces justificatives augmentent la fiabilité du dossier
              </h2>
              <p className="mt-1 text-xs text-[#5B5B62] max-w-2xl leading-relaxed">
                Chaque document téléversé est chiffré, isolé et lié à une section.
                Il permet à ALTIORA de distinguer ce qui est <em>déclaré</em> de ce qui est
                <em> documenté</em>, <em>confirmé</em> ou <em>vérifié</em>.
              </p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <VaultDocuments
              dossierId={dossier.dossier_id}
              category="formation"
              title="Formation & diplômes"
              helpText="Diplômes, relevés de notes, certifications, équivalences, permis professionnels."
              linkedSections={["parcours.formation"]}
              subcategoryOptions={[
                { value: "diplome", label: "Diplôme" },
                { value: "releve_notes", label: "Relevé de notes" },
                { value: "certification", label: "Certification" },
                { value: "equivalence", label: "Équivalence / reconnaissance" },
                { value: "permis_pro", label: "Permis professionnel" },
                { value: "certificat_langue", label: "Certificat linguistique" },
              ]}
            />
            <VaultDocuments
              dossierId={dossier.dossier_id}
              category="experience"
              title="Expérience & CV"
              helpText="CV, contrats, lettres d'emploi, attestations, lettres de recommandation, portfolio."
              linkedSections={["parcours.professional", "personal.employment"]}
              subcategoryOptions={[
                { value: "cv", label: "CV" },
                { value: "contrat", label: "Contrat de travail" },
                { value: "attestation_exp", label: "Attestation d'expérience" },
                { value: "lettre_emploi", label: "Lettre d'emploi" },
                { value: "lettre_reco", label: "Lettre de recommandation" },
                { value: "portfolio", label: "Portfolio" },
              ]}
            />
            <VaultDocuments
              dossierId={dossier.dossier_id}
              category="fiscalite"
              title="Fiscalité"
              helpText="Déclarations fiscales, avis de cotisation, relevés d'impôts, certificats de conformité."
              linkedSections={["finances.tax", "finances.income"]}
              subcategoryOptions={[
                { value: "declaration", label: "Déclaration de revenus" },
                { value: "avis_cotisation", label: "Avis de cotisation" },
                { value: "declaration_entreprise", label: "Déclaration d'entreprise" },
                { value: "attestation_conformite", label: "Attestation de conformité" },
                { value: "retenues_source", label: "Retenues à la source" },
              ]}
            />
          </div>
        </section>

        {/* Historique — 10 derniers Bilans PDF avec rendu reproductible */}
        <DossierBilanHistory dossier={dossier} />

        {/* Sub-dossiers section (children + create-child archetypes) */}
        {(children.length > 0 || childArchetypes.length > 0) && (
          <section className="mt-12" data-testid="dossier-children-section">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">Dossiers liés</div>
                <h2 className="mt-1 font-display text-2xl tracking-tight text-[#1F2937]">
                  {children.length} sous-dossier{children.length > 1 ? "s" : ""}
                </h2>
              </div>
            </div>

            {children.length > 0 && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dossier-children-list">
                {children.map((c) => (
                  <Link
                    key={c.dossier_id}
                    to={`/app/dossiers/${c.dossier_id}`}
                    data-testid={`dossier-child-${c.dossier_id}`}
                    className="rounded-xl border border-[#E4E1D8] bg-white hover:border-[#0B2545] p-4 transition-all group"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-display text-sm text-[#1F2937] tracking-tight truncate">{c.name}</div>
                      <ArrowRight className="h-4 w-4 text-[#B8B5AC] group-hover:text-[#0B2545] group-hover:translate-x-0.5 transition-all" strokeWidth={1.5} />
                    </div>
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                      {c.type === "individual" ? "Personne" : "Organisation"} · {c.subtype || "sous-dossier"}
                      {c.status === "pending_client_approval" && (
                        <span className="ml-2 bg-[#FBF6EA] text-[#7A5B14] rounded-full px-1.5 py-0.5">approbation requise</span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {childArchetypes.length > 0 && (
              <div className="mt-6">
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#5B5B62]">
                  Créer un nouveau sous-dossier
                </div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="dossier-children-cta">
                  {childArchetypes.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => openChildFlow(c)}
                      data-testid={`dossier-create-child-${c.key}`}
                      className="inline-flex items-center gap-2 rounded-md border border-[#E4E1D8] bg-white hover:border-[#0B2545] hover:shadow-sm px-3.5 py-2 text-sm text-[#1F2937] transition-all group"
                    >
                      <FolderPlus className="h-3.5 w-3.5 text-[#0B2545]" strokeWidth={2} />
                      {c.label_fr}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

function ModuleCard({ module: m, active, testId, highlight = false }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border bg-white p-5 shadow-sm relative overflow-hidden transition-all cursor-pointer ${
        highlight ? "border-[#0B2545] shadow-md scale-[1.01]" : "border-[#E4E1D8] hover:border-[#0B2545]/50"
      }`}
      style={{ borderLeft: `4px solid ${m.color_accent}` }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-display text-lg text-[#1F2937] tracking-tight">{m.name_fr}</div>
            {active && (
              <CircleCheck className="h-4 w-4 text-[#059669]" strokeWidth={2} />
            )}
          </div>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
            {m.category} · {m.subscription_tier}
          </div>
          <p className="mt-3 text-sm text-[#5B5B62] leading-relaxed">{m.description_fr}</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        {m.self_service && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#065F46] bg-[#D1FAE5] rounded-full px-2 py-0.5">
            <Sparkles className="h-2.5 w-2.5" strokeWidth={2} /> Libre-service
          </span>
        )}
        {m.required_licenses?.length > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] bg-[#FBF6EA] rounded-full px-2 py-0.5">
            <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2} /> Habilitation pro
          </span>
        )}
      </div>
    </div>
  );
}
